/**
 * @file auth.ts
 * @description Frontend auth store and Astro middleware utilities for Clerk integration.
 * @author [F. Andy Seidl](https://www.linkedin.com/in/faseidl/)
 * @license UNLICENSED
 */

import { DEFAULT_CLERK_SIGN_IN_PATH } from './routes.js';
import { escapeHtmlAttribute } from './display/sanitization.js';

/*======================================================================
 * Authentication Constants
 *======================================================================*/

export { DEFAULT_CLERK_SIGN_IN_PATH };

/**
 * Default live Clerk publishable key for CountTheTime production.
 */
export const DEFAULT_CLERK_PUBLISHABLE_KEY = 'pk_live_Y2xlcmsuY291bnR0aGV0aW1lLmNvbSQ';

/**
 * Default custom domain URL for Clerk browser JavaScript SDK.
 */
export const DEFAULT_CLERK_JS_URL =
  'https://clerk.countthetime.com/npm/@clerk/clerk-js@5/dist/clerk.browser.js';

/**
 * Default cookie name storing Clerk session authentication tokens.
 */
export const DEFAULT_CLERK_COOKIE_NAME = 'clerk-session';

/**
 * Standard first-party cookie name set natively by Clerk JavaScript SDK.
 */
export const CLERK_NATIVE_SESSION_COOKIE_NAME = '__session';

/**
 * Generates the HTML script tag for loading the Clerk browser SDK.
 *
 * @param publishableKey - Clerk instance publishable API key.
 * @param scriptUrl - Fully qualified URL to the Clerk browser bundle.
 * @param elementId - Optional DOM element identifier for the script element.
 * @returns Serialized HTML script element.
 */
export function getClerkScriptTag(
  publishableKey: string,
  scriptUrl: string,
  elementId?: string,
): string {
  const idAttr = elementId ? ` id="${escapeHtmlAttribute(elementId)}"` : '';
  return `<script async crossorigin="anonymous"${idAttr} data-clerk-publishable-key="${escapeHtmlAttribute(publishableKey)}" src="${escapeHtmlAttribute(scriptUrl)}" type="text/javascript"></script>`;
}

/*======================================================================
 * Types & Interfaces
 *======================================================================*/

export interface UserProfile {
  id: string;
  fullName?: string;
  primaryEmail?: string;
  [key: string]: unknown;
}

export interface AuthStore {
  isSignedIn: boolean;
  user: UserProfile | null;
  signInWithMock: (user: UserProfile, token: string) => Promise<void>;
  signOut: () => void;
  getToken: () => Promise<string | null>;
}

export interface AstroRequestContext {
  url: URL;
  locals: {
    userId?: string;
    user?: UserProfile;
    cookies?: {
      get: (name: string) => { value: string | undefined } | undefined;
    };
    [key: string]: unknown;
  };
  redirect: (path: string, status?: number) => Response;
}

export type AstroMiddlewareNext = () => Promise<Response> | Response;

export interface ClerkAstroMiddlewareOptions {
  protectedPaths: string[];
  /**
   * Redirect path for unauthenticated users.
   * @defaultValue {@link DEFAULT_CLERK_SIGN_IN_PATH}
   */
  signInPath?: string;
  /**
   * Session cookie name for token resolution.
   * @defaultValue {@link DEFAULT_CLERK_COOKIE_NAME}
   */
  cookieName?: string;
}

/*======================================================================
 * Reactive Auth Store
 *======================================================================*/

/**
 * Creates a reactive-like client-side authentication store.
 *
 * @returns An instance of {@link AuthStore}.
 */
export function createAuthStore(): AuthStore {
  let isSignedIn = false;
  let user: UserProfile | null = null;
  let token: string | null = null;

  return {
    get isSignedIn() {
      return isSignedIn;
    },
    get user() {
      return user;
    },
    async signInWithMock(newUser: UserProfile, newToken: string): Promise<void> {
      isSignedIn = true;
      user = newUser;
      token = newToken;
    },
    signOut() {
      isSignedIn = false;
      user = null;
      token = null;
    },
    async getToken(): Promise<string | null> {
      return token;
    },
  };
}

/*======================================================================
 * Astro Middleware Guard
 *======================================================================*/

/**
 * Astro middleware to authenticate and guard routes with Clerk.
 *
 * @param options - Configuration options for path protection.
 * @returns Astro middleware function.
 */
export function clerkAstroMiddleware(
  options: ClerkAstroMiddlewareOptions,
): (context: AstroRequestContext, next: AstroMiddlewareNext) => Promise<Response> | Response {
  const protectedPaths = options.protectedPaths || [];
  const signInPath = options.signInPath || DEFAULT_CLERK_SIGN_IN_PATH;
  const cookieName = options.cookieName || DEFAULT_CLERK_COOKIE_NAME;

  return async (context, next) => {
    const pathname = context.url.pathname;
    const isProtected = protectedPaths.some((p) => pathname.startsWith(p));

    // Retrieve session cookie from context.locals if cookies API is mocked/provided
    let sessionToken: string | undefined;
    if (context.locals.cookies && typeof context.locals.cookies.get === 'function') {
      const cookieObj = context.locals.cookies.get(cookieName);
      sessionToken = cookieObj?.value;
    }

    if (isProtected && !sessionToken) {
      return context.redirect(signInPath);
    }

    if (sessionToken) {
      context.locals.userId = sessionToken;
      context.locals.user = {
        id: sessionToken,
      };
    }

    return await next();
  };
}

/*======================================================================
 * Authenticated HTTP Client & Storage Keys
 *======================================================================*/

/**
 * LocalStorage key persisting the active dev bearer token string.
 */
export const DEV_PERSONA_TOKEN_STORAGE_KEY = 'ctt_dev_bearer_token' as const;

/**
 * A client-side fetch wrapper that automatically retrieves the active JWT token from the AuthStore
 * and injects it into the request's Authorization header as a Bearer token.
 *
 * In local development, if no token is available from the AuthStore, it falls back to inspecting
 * the dev persona bearer token saved in localStorage.
 *
 * @param url - The target API URL or request endpoint.
 * @param init - Request configuration options.
 * @param store - The active {@link AuthStore} instance.
 * @param fetchFn - Optional custom fetch function override (defaults to globalThis.fetch).
 * @returns The Promise resolving to the fetch Response.
 */
export async function authenticatedFetch(
  url: string | URL,
  init?: RequestInit,
  store?: AuthStore | null,
  fetchFn: typeof fetch = globalThis.fetch,
): Promise<Response> {
  const headers = new Headers(init?.headers);

  let token: string | null = null;
  if (store) {
    token = await store.getToken();
  }

  // Fallback to dev persona token from localStorage if in browser environment and not provided by store
  if (!token && typeof window !== 'undefined' && typeof window.localStorage !== 'undefined') {
    try {
      token = window.localStorage.getItem(DEV_PERSONA_TOKEN_STORAGE_KEY);
    } catch {
      // In restricted or sandboxed iframe environments, localStorage access might throw
    }
  }

  // Fallback to active Clerk session token if in browser environment and not provided by store or dev token
  if (!token && typeof window !== 'undefined') {
    try {
      const globalClerk = (
        window as unknown as {
          Clerk?: { session?: { getToken: () => Promise<string | null> } };
        }
      ).Clerk;
      if (globalClerk?.session?.getToken) {
        token = await globalClerk.session.getToken();
      }
    } catch {
      // In restricted environments or if Clerk threw an error, continue without Authorization header
    }
  }

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const updatedInit: RequestInit = {
    ...init,
    headers,
  };

  return await fetchFn(url, updatedInit);
}
